import type { Factor } from '@supabase/supabase-js';
import { supabaseClient } from './supabase';

export function totpQrCodeDataUrl(qrCode: string): string {
    if (qrCode.startsWith('data:')) {
        return qrCode;
    }
    if (qrCode.trimStart().startsWith('<svg')) {
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrCode)}`;
    }
    return qrCode;
}

export async function listAllTotpFactors(): Promise<Factor[]> {
    const { data, error } = await supabaseClient.auth.mfa.listFactors();
    if (error) throw error;
    return (data?.all ?? []).filter((factor) => factor.factor_type === 'totp');
}

export async function listVerifiedTotpFactors(): Promise<Factor[]> {
    const factors = await listAllTotpFactors();
    return factors.filter((factor) => factor.status === 'verified');
}

export async function cleanupUnverifiedTotpFactors(): Promise<void> {
    const factors = await listAllTotpFactors();
    const unverified = factors.filter((factor) => factor.status === 'unverified');

    for (const factor of unverified) {
        const { error } = await supabaseClient.auth.mfa.unenroll({ factorId: factor.id });
        if (error) throw error;
    }
}

export async function enrollTotpFactor(friendlyName = 'Authenticator app'): Promise<{
    factorId: string;
    qrCode: string;
    secret: string;
}> {
    await cleanupUnverifiedTotpFactors();

    const { data, error } = await supabaseClient.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName,
    });
    if (error) throw error;
    if (!data?.totp?.qr_code || !data.totp.secret) {
        throw new Error('Authenticator enrollment did not return a QR code.');
    }

    return {
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
    };
}

export async function verifyTotpEnrollment(factorId: string, code: string): Promise<void> {
    const { data: challenge, error: challengeError } = await supabaseClient.auth.mfa.challenge({
        factorId,
    });
    if (challengeError) throw challengeError;

    const { error } = await supabaseClient.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
    });
    if (error) throw error;
}

export async function unenrollTotpFactor(factorId: string): Promise<void> {
    const { error } = await supabaseClient.auth.mfa.unenroll({ factorId });
    if (error) throw error;
}

export async function verifyMfaChallenge(code: string): Promise<void> {
    const factors = await listVerifiedTotpFactors();
    const totpFactor = factors[0];
    if (!totpFactor) {
        throw new Error('No authenticator app is enrolled for this account.');
    }

    const { data: challenge, error: challengeError } = await supabaseClient.auth.mfa.challenge({
        factorId: totpFactor.id,
    });
    if (challengeError) throw challengeError;

    const { error } = await supabaseClient.auth.mfa.verify({
        factorId: totpFactor.id,
        challengeId: challenge.id,
        code,
    });
    if (error) throw error;
}

export async function needsMfaVerification(): Promise<boolean> {
    const { data, error } = await supabaseClient.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) throw error;
    return data.nextLevel === 'aal2' && data.currentLevel !== data.nextLevel;
}

export async function hasSecuritySecondFactor(): Promise<boolean> {
    const totpFactors = await listVerifiedTotpFactors();
    return totpFactors.length > 0;
}

export async function needsSecurityEnrollment(): Promise<boolean> {
    return !(await hasSecuritySecondFactor());
}
